/**
 * Alerta na aba do navegador: prefixo de contador no título + badge no favicon.
 *
 * Serve de fallback universal para quando o usuário está com a janela fora de
 * foco e o som/popup nativo pode não chegar (permissão negada, volume mudo,
 * contexto inseguro). O título é estático — nada de piscar via setInterval,
 * porque o navegador estrangula timers justamente em abas de segundo plano.
 */

import { i18n } from "../translate/i18n";

const ORIGINAL_TITLE = document.title;
const FAVICON_SIZE = 64;

let unreadCount = 0;
let originalFaviconHref = null;
let badgedFaviconHref = null;

const getFaviconLinks = () =>
  Array.from(document.querySelectorAll("link[rel~='icon']"));

const setFaviconHref = href => {
  if (!href) return;
  getFaviconLinks().forEach(link => {
    link.href = href;
  });
};

const drawBadgedFavicon = sourceImage => {
  const canvas = document.createElement("canvas");
  canvas.width = FAVICON_SIZE;
  canvas.height = FAVICON_SIZE;

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  if (sourceImage) {
    ctx.drawImage(sourceImage, 0, 0, FAVICON_SIZE, FAVICON_SIZE);
  }

  const radius = FAVICON_SIZE * 0.3;
  const center = FAVICON_SIZE - radius - 2;

  ctx.beginPath();
  ctx.arc(center, center, radius, 0, 2 * Math.PI);
  ctx.fillStyle = "#e53935";
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.strokeStyle = "#ffffff";
  ctx.stroke();

  return canvas.toDataURL("image/png");
};

/**
 * O favicon com badge é gerado uma única vez e reaproveitado — o número fica
 * só no título, o ícone carrega apenas o ponto vermelho (não há espaço útil
 * para dígitos legíveis em 16x16).
 */
const ensureBadgedFavicon = () =>
  new Promise(resolve => {
    if (badgedFaviconHref) {
      resolve(badgedFaviconHref);
      return;
    }

    const [link] = getFaviconLinks();
    if (originalFaviconHref === null) {
      originalFaviconHref = link ? link.href : null;
    }

    if (!originalFaviconHref) {
      badgedFaviconHref = drawBadgedFavicon(null);
      resolve(badgedFaviconHref);
      return;
    }

    const image = new Image();
    image.onload = () => {
      badgedFaviconHref = drawBadgedFavicon(image);
      resolve(badgedFaviconHref);
    };
    image.onerror = () => {
      badgedFaviconHref = drawBadgedFavicon(null);
      resolve(badgedFaviconHref);
    };
    image.src = originalFaviconHref;
  });

const render = () => {
  if (unreadCount <= 0) {
    document.title = ORIGINAL_TITLE;
    setFaviconHref(originalFaviconHref);
    return;
  }

  const label = unreadCount === 1
    ? i18n.t("tabAlert.newMessage")
    : i18n.t("tabAlert.newMessages");
  document.title = `(${unreadCount}) ${label} · ${ORIGINAL_TITLE}`;
  ensureBadgedFavicon().then(setFaviconHref);
};

export const bump = () => {
  if (document.hasFocus()) return;
  unreadCount += 1;
  render();
};

export const reset = () => {
  if (unreadCount === 0) return;
  unreadCount = 0;
  render();
};

window.addEventListener("focus", reset);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") reset();
});

export default { bump, reset };
